import React from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import { CONTACT_INFO } from "../data";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";

export default function Contact() {
  const { lang, t } = useLanguage();

  const homePath = lang === "da" ? "/" : "/en";
  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const projectsPath = lang === "da" ? "/projekter" : "/en/projects";
  const aboutPath = lang === "da" ? "/om-os" : "/en/about";

  // Custom SEO titles and descriptions for Contact page
  const seoTitle = lang === "da" 
    ? "Kontakt os | CPH Collective | Start dit projekt" 
    : "Contact Us | CPH Collective | Start Your Project";
  const seoDesc = lang === "da"
    ? "Kontakt CPH Collective for et uforpligtende tilbud på dit næste bygge- eller tømrerprojekt i København og på Sjælland."
    : "Get in touch with CPH Collective to request a non-binding quote or consultation for your construction project in Copenhagen.";

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24 md:pt-32">
      {/* SEO Tags */}
      <SEO title={seoTitle} description={seoDesc} />

      {/* Header Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl">
            <FadeIn direction="up">
              <span className="text-xs uppercase tracking-[0.4em] text-stone-500 font-mono block mb-4">
                {t("contact.header.tag")}
              </span>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 uppercase mb-8">
                {t("contact.header.title")}
              </h1>
              <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
                {lang === "da" ? (
                  <>
                    Kontakt{" "}
                    <Link
                      to={homePath}
                      className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                    >
                      CPH Collective
                    </Link>{" "}
                    for en uforpligtende samtale om dit bygge- eller tømrerprojekt. Vi rådgiver, koordinerer og udarbejder præcise tilbud baseret på dine behov og projektets omfang.
                  </>
                ) : (
                  <>
                    Contact{" "}
                    <Link
                      to={homePath}
                      className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                    >
                      CPH Collective
                    </Link>{" "}
                    for a non-binding conversation about your construction or carpentry project. We provide guidance, architectural coordination, and prepare precise quotes based on your needs and project scope.
                  </>
                )}
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Contact Content Grid */}
      <section className="py-24 md:py-32">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
            {/* Left Column: Contact Info & Map */}
            <div className="lg:col-span-5">
              <FadeIn direction="right">
                <div className="space-y-12">
                  <div>
                    <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                      {t("contact.info.number")}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-light uppercase tracking-wider text-stone-900 mb-8">
                      {t("contact.info.title")}
                    </h2>

                    <div className="space-y-6">
                      {/* Address */}
                      <div className="flex items-start gap-4">
                        <MapPin className="w-5 h-5 text-stone-500 shrink-0 mt-1" />
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-stone-500 font-mono mb-1">
                            {t("contact.info.address")}
                          </h4>
                          <p className="text-stone-700 text-sm leading-relaxed">
                            {CONTACT_INFO.address}
                          </p>
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="flex items-start gap-4">
                        <Phone className="w-5 h-5 text-stone-500 shrink-0 mt-1" />
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-stone-500 font-mono mb-1">
                            {t("contact.info.phone")}
                          </h4>
                          <p className="text-stone-700 text-sm font-mono">
                            <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-stone-900 transition-colors">
                              {CONTACT_INFO.phone}
                            </a>
                          </p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start gap-4">
                        <Mail className="w-5 h-5 text-stone-500 shrink-0 mt-1" />
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-stone-500 font-mono mb-1">
                            {t("contact.info.email")}
                          </h4>
                          <p className="text-stone-700 text-sm font-mono">
                            <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-stone-900 transition-colors">
                              {CONTACT_INFO.email}
                            </a>
                          </p>
                        </div>
                      </div>

                      {/* Hours */}
                      <div className="flex items-start gap-4">
                        <Clock className="w-5 h-5 text-stone-500 shrink-0 mt-1" />
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-stone-500 font-mono mb-1">
                            {t("contact.info.hours")}
                          </h4>
                          <p className="text-stone-700 text-sm">
                            {CONTACT_INFO.hours[lang]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <FadeIn direction="left" delay={150}>
                <ContactForm />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
      {/* Cross-links before footer */}
      <section className="py-20 bg-stone-100/70 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-2">
              {lang === "da" ? "Før du kontakter os" : "Before you reach out"}
            </span>
            <h3 className="text-2xl font-light uppercase tracking-tight text-stone-900">
              {lang === "da" ? "Udforsk CPH Collective" : "Explore CPH Collective"}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-stone-400 block mb-2">01</span>
                <h4 className="text-lg font-light uppercase tracking-wide text-stone-900 mb-2">
                  {lang === "da" ? "Vores Ydelser" : "Our Services"}
                </h4>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Se alt om totalentreprise, projektering, tømrerarbejde og byggepladsservice."
                    : "Learn about our turnkey solutions, engineering, carpentry, and site services."}
                </p>
              </div>
              <Link
                to={servicesPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Se ydelser" : "View services"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-stone-400 block mb-2">02</span>
                <h4 className="text-lg font-light uppercase tracking-wide text-stone-900 mb-2">
                  {lang === "da" ? "Referencer" : "Case Studies"}
                </h4>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Gennemse billeder og beskrivelser af vores afleverede bygge- og tømrerprojekter."
                    : "Browse photography and case studies of our delivered construction projects."}
                </p>
              </div>
              <Link
                to={projectsPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Se projekter" : "View projects"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-stone-400 block mb-2">03</span>
                <h4 className="text-lg font-light uppercase tracking-wide text-stone-900 mb-2">
                  {lang === "da" ? "Om Virksomheden" : "About Us"}
                </h4>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Læs om vores fokus på præcision, kvalitetssikring, tidsplaner og budgetoverholdelse."
                    : "Discover our dedication to precision, quality control, strict scheduling, and budgets."}
                </p>
              </div>
              <Link
                to={aboutPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Læs om os" : "About us"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
